import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { desktop_breakpoint } from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import PlusCircleSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/plus-circle.svg';
import ShareBranchSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/share-branch.svg';
import StudioWrapper from '../StudioWrapper';
import Modal from '@components/Shared/Modal/Modal';
import StructuredData from '@components/Studio/StructuredData/StructuredData';
import BodyContent from '@components/Studio/BodyContent/BodyContent';
import EmailWebForm from '../EmailWebForm/EmailWebForm';
import PostPageCTA from '@components/Studio/PostPageCTA/PostPageCTA';
import PostCard from '../PostCard/PostCard';
import SocialShare from '@components/Studio/SocialShare/SocialShare';
import HomeStream from '@components/Studio/HomeStream/HomeStream';
import PreFooterCTA from '../PreFooterCTA/PreFooterCTA';
import { categoryNameSlug } from '@utils/studioUtils';
import PostPageStyles from './Post.styled';
import PostContentModules from './PostContentModules';
import { formatHttpsUrl } from '@utils/utils';
import { getSiteUrl } from '@utils/config';

const Post = ({ post, summary, curatedButtons = [] }) => {
  const subscribeFormRef = useRef();
  const mainImageURL = formatHttpsUrl(
    `${post.featuredMedia?.media?.file?.url}?w=800&fm=webp`
  );
  const [showShare, setShowShare] = useState(false);

  const subscribeClick = () => {
    if (subscribeFormRef.current) {
      subscribeFormRef.current.focus();
    }
  };

  const router = useRouter();

  return (
    <StudioWrapper
      key={post.slug}
      posts={summary.allPosts}
      curatedButtons={curatedButtons}
    >
      <PostPageStyles>
        <Head>
          <title>{post.titleTag}</title>
          <meta property="og:type" content="article" />
          <meta
            property="og:image"
            content={post.featuredMedia.media.file.url}
          />
          <meta property="og:title" content={post.headline} />
          <meta
            property="og:description"
            content={post.copyBrief ? post.copyBrief : post.headline}
          />
          <link
            rel="canonical"
            href={`${getSiteUrl(
              router.asPath.endsWith('/') ? router.asPath : router.asPath + '/'
            )}`}
          />
        </Head>

        <StructuredData post={post} imageUrl={mainImageURL} />
        <div className="post-container">
          {post.postSubcategory ? (
            <Link
              href={`/studio/${categoryNameSlug(post.postSubcategory)}`}
              className="post-category"
            >
              {post.postSubcategory}
            </Link>
          ) : (
            'no category'
          )}

          <h1 className="post-title">{post.headline}</h1>
          <div className="post-header">
            <div className="post-authors">
              {post.authorByline.map((author, index) => {
                const avatarImage = formatHttpsUrl(
                  `${author.authorImage?.file?.url}?w=50&q=100`
                ); //desktop
                const avatarImage2x = formatHttpsUrl(
                  `${author.authorImage?.file?.url}?w=100&q=100`
                ); //mobile

                return (
                  <Link
                    key={index}
                    href={`/studio/${author.slug}`}
                    className="post-author"
                  >
                    <picture>
                      <source
                        media={`(min-width:${desktop_breakpoint}px)`}
                        className="post-author-image"
                        alt={author.authorName}
                        title={author.authorName}
                        srcSet={avatarImage}
                      />
                      <Image
                        className="post-author-image"
                        alt={author.authorName}
                        title={author.authorName}
                        src={avatarImage2x}
                        width={50}
                        height={50}
                      />
                    </picture>
                    <div className="post-author-info">
                      <span className="post-author-name intro">
                        {author.authorName}
                      </span>
                      {index > 0 ? (
                        <span className="post-info caption"></span>
                      ) : (
                        <span className="post-info caption">
                          {new Date(post.pubDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            timeZone: 'UTC',
                          })}{' '}
                          | {post.readTime} minute read
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="post-actions">
              <div className="subscribe-svg">
                <PlusCircleSvg
                  onClick={subscribeClick}
                  viewBox="0 0 16 16"
                  tabIndex="0"
                />
              </div>
              <div className="share-svg">
                <ShareBranchSvg
                  onClick={() => setShowShare(true)}
                  viewBox="0 0 16 16"
                  tabIndex="0"
                />
              </div>
            </div>
          </div>

          {mainImageURL && (
            <div className="post-article-image">
              <Image
                src={mainImageURL}
                alt={post.featuredMedia.altText}
                title={post.featuredMedia.description}
                width={800}
                height={533}
              />
            </div>
          )}

          {post.copyBrief && (
            <p className="post-copy-brief intro">{post.copyBrief}</p>
          )}

          {post.uniquePostContent1 && (
            <BodyContent markdown={post.uniquePostContent1} />
          )}

          {/* This empty paragraph prevents CSS Modules from removing styling below the video. */}
          {post && post.reuseContentModules ? <p></p> : ''}
          {post && post.reuseContentModules ? (
            <PostContentModules post={post} />
          ) : (
            ''
          )}

          {post.uniquePostContent2 && (
            <div className="unique-post-content-2">
              <BodyContent markdown={post.uniquePostContent2} />
            </div>
          )}

          {post.citation && post.citation.bodyText && (
            <div className="citation">
              <BodyContent markdown={post.citation.bodyText} />
            </div>
          )}
        </div>

        {post.contentCTAs ? <PostPageCTA ctas={post.contentCTAs} /> : null}

        {post.relatedPostPages && post.relatedPostPages.length > 0 && (
          <div className="related-posts">
            <div className="constrained-width">
              <h3>Related Posts</h3>
              <div className="related-posts-list">
                {post.relatedPostPages.slice(0, 3).map((post, index) => {
                  return <PostCard key={index} post={post} />;
                })}
              </div>
            </div>
          </div>
        )}

        {post.curatedCollectionStream && (
          <HomeStream
            internallyLinked={false}
            stream={post.curatedCollectionStream}
          />
        )}

        {post && post.webForm ? (
          <EmailWebForm
            webFormData={post.webForm}
            formRef={subscribeFormRef}
            pageType="studio_post"
          />
        ) : null}

        {post.preFooterCta && <PreFooterCTA preFooterCTA={post.preFooterCta} />}

        {showShare && (
          <Modal onClose={() => setShowShare(false)}>
            <SocialShare post={post} />
          </Modal>
        )}
      </PostPageStyles>
    </StudioWrapper>
  );
};

Post.propTypes = {
  curatedButtons: PropTypes.array,
  post: PropTypes.shape({
    authorByline: PropTypes.array,
    citation: PropTypes.shape({
      bodyText: PropTypes.any,
    }),
    contentCTAs: PropTypes.any,
    copyBrief: PropTypes.string,
    curatedCollectionStream: PropTypes.any,
    featuredMedia: PropTypes.shape({
      altText: PropTypes.any,
      description: PropTypes.any,
      media: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.any,
        }),
      }),
    }),
    headline: PropTypes.string,
    postSubcategory: PropTypes.any,
    preFooterCta: PropTypes.any,
    pubDate: PropTypes.string,
    readTime: PropTypes.any,
    relatedPostPages: PropTypes.array,
    reuseContentModules: PropTypes.array,
    slug: PropTypes.string,
    titleTag: PropTypes.any,
    uniquePostContent1: PropTypes.any,
    uniquePostContent2: PropTypes.any,
    webForm: PropTypes.any,
  }),
  posts: PropTypes.bool,
  summary: PropTypes.shape({
    allPosts: PropTypes.any,
  }),
};

export default Post;
